import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { AppFooter } from '../../shared/AppFooter';
import { AppHeader } from '../../shared/AppHeader';
import { useRegister } from '../../../context/RegisterContext';
import { CHECKBOX_TERMOS, EMAIL_FIELD, REQUIRED_FIELD } from '../../../utils/constants';
import { PARTNER_BENEFITS } from '../../../utils/homeData';
import partnerVisibilityImg from '../../../assets/new-design/partner-visibility.png';
import partnerSupportImg from '../../../assets/new-design/partner-support.png';
import logoLarge from '../../../assets/new-design/logo-large.png';
import './Parceiros.scss';

const schema = yup.object({
  full_name: yup
    .string()
    .required(REQUIRED_FIELD)
    .min(2, 'Nome deve ter pelo menos 2 caracteres')
    .matches(/^[a-zA-ZÀ-ÿ\s]+$/, 'Nome deve conter apenas letras e espaços')
    .test('not-empty', 'Nome não pode ser apenas espaços', (v) => (v ? v.trim().length > 0 : false))
    .transform((v) => v?.trim()),
  last_name: yup
    .string()
    .required(REQUIRED_FIELD)
    .min(2, 'Sobrenome deve ter pelo menos 2 caracteres')
    .matches(/^[a-zA-ZÀ-ÿ\s]+$/, 'Sobrenome deve conter apenas letras e espaços')
    .test('not-empty', 'Sobrenome não pode ser apenas espaços', (v) => (v ? v.trim().length > 0 : false))
    .transform((v) => v?.trim()),
  email: yup.string().required(REQUIRED_FIELD).email(EMAIL_FIELD).transform((v) => v?.toLowerCase().trim()),
  phone: yup.string().required(REQUIRED_FIELD),
  receive_communications: yup.bool().optional(),
  privacy_terms: yup.bool().required(CHECKBOX_TERMOS).isTrue(CHECKBOX_TERMOS),
});

type SignUpForm = yup.InferType<typeof schema>;

export default function ParceirosLP() {
  const navigate = useNavigate();
  const { registerHeroSignUp } = useRegister();
  const [submitted, setSubmitted] = useState(false);
  const formCardRef = useRef<HTMLDivElement>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpForm>({ resolver: yupResolver(schema) });

  const onSubmit = (data: yup.InferType<typeof schema>) => {
    registerHeroSignUp(data as any);
    setSubmitted(true);
    setTimeout(() => navigate('/register/address'), 1500);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, []);

  const scrollToForm = () => {
    formCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="parceiros-page">
      <AppHeader variant="parceiro" />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="parceiros-hero" style={{ paddingTop: '132px' }}>
        <div className="parceiros-hero__overlay" />
        <div className="parceiros-hero__inner">
          <div>
            <img src={logoLarge} alt="WashPass" className="parceiros-hero__logo" />
            <h1 className="parceiros-hero__title">Atenda a maior frota de carros do Brasil!</h1>
            <div className="parceiros-hero__divider" />
            <p className="parceiros-hero__desc">
              Seja parte da maior frota de carros do Brasil e aumente os resultados do seu lava-rápido, atendendo a milhares de motoristas que buscam serviços de alta qualidade. Conecte-se com a maior frota de veículos do Brasil e faça seu empreendimento crescer.
            </p>
          </div>

          <div id="cadastro-parceiro" ref={formCardRef} className="parceiros-hero__form-card">
            {submitted ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div className="parceiros-hero__success-icon">✓</div>
                <div className="parceiros-hero__success-title">Cadastro recebido!</div>
                <div className="parceiros-hero__success-desc">Nosso time de parceiros vai entrar em contato em breve.</div>
              </div>
            ) : (
              <>
                <h3 className="parceiros-hero__form-title">
                  Cadastre-se e aumente a visibilidade do seu lava-rápido!
                </h3>
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <Controller
                    name="full_name"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <>
                        <label className="parceiros-form__label">Nome*</label>
                        <input
                          {...field}
                          type="text"
                          placeholder="Nome do Responsável"
                          className={`parceiros-form__input${errors.full_name ? ' parceiros-form__input--error' : ''}`}
                        />
                        {errors.full_name && <span className="parceiros-form__error">{errors.full_name.message}</span>}
                      </>
                    )}
                  />

                  <Controller
                    name="last_name"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <>
                        <label className="parceiros-form__label">Sobrenome*</label>
                        <input
                          {...field}
                          type="text"
                          placeholder="Sobrenome do Responsável"
                          className={`parceiros-form__input${errors.last_name ? ' parceiros-form__input--error' : ''}`}
                        />
                        {errors.last_name && <span className="parceiros-form__error">{errors.last_name.message}</span>}
                      </>
                    )}
                  />

                  <Controller
                    name="email"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <>
                        <label className="parceiros-form__label">E-mail*</label>
                        <input
                          {...field}
                          type="email"
                          placeholder="E-mail do Responsável"
                          className={`parceiros-form__input${errors.email ? ' parceiros-form__input--error' : ''}`}
                        />
                        {errors.email && <span className="parceiros-form__error">{errors.email.message}</span>}
                      </>
                    )}
                  />

                  <Controller
                    name="phone"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <>
                        <label className="parceiros-form__label">Telefone*</label>
                        <input
                          {...field}
                          type="text"
                          placeholder="(11) 90000-0000"
                          className={`parceiros-form__input${errors.phone ? ' parceiros-form__input--error' : ''}`}
                        />
                        {errors.phone && <span className="parceiros-form__error">{errors.phone.message}</span>}
                      </>
                    )}
                  />

                  <Controller
                    name="receive_communications"
                    control={control}
                    render={({ field }) => (
                      <label className="parceiros-form__checkbox-label">
                        <input type="checkbox" checked={field.value ?? false} onChange={field.onChange} />
                        <span>Eu concordo em receber comunicações.</span>
                      </label>
                    )}
                  />

                  <Controller
                    name="privacy_terms"
                    control={control}
                    render={({ field }) => (
                      <>
                        <label className="parceiros-form__checkbox-label">
                          <input type="checkbox" checked={field.value ?? false} onChange={field.onChange} />
                          <span>
                            Ao informar meus dados, estou ciente e concordo com as diretrizes da{' '}
                            <a href="/privacy-policy">Política de privacidade</a>.
                          </span>
                        </label>
                        {errors.privacy_terms && <span className="parceiros-form__error">{errors.privacy_terms.message}</span>}
                      </>
                    )}
                  />

                  <button type="submit" className="parceiros-form__submit">Cadastrar</button>
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ── BENEFITS ─────────────────────────────────────────────────────── */}
      <section className="parceiros-benefits">
        <h2 className="parceiros-benefits__title">Por que se tornar um parceiro WashPass?</h2>
        <div className="parceiros-benefits__grid">
          {PARTNER_BENEFITS.map((benefit) => (
            <div key={benefit.title} className="parceiros-benefits__item">
              <img src={benefit.imgSrc} alt={benefit.title} className="parceiros-benefits__item-img" />
              <div className="parceiros-benefits__item-title">{benefit.title}</div>
              <div className="parceiros-benefits__item-desc">{benefit.desc}</div>
              <button className="parceiros-benefits__item-btn" onClick={scrollToForm}>
                {benefit.cta}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ── VISIBILITY ───────────────────────────────────────────────────── */}
      <section className="parceiros-visibility">
        <div className="parceiros-visibility__inner">
          <img src={partnerVisibilityImg} alt="Cliente WashPass" className="parceiros-visibility__img" />
          <div>
            <h2 className="parceiros-visibility__title">Aumente a visibilidade do seu lava-rápido!</h2>
            <p className="parceiros-visibility__desc">
              Cadastre-se agora e faça parte de uma rede exclusiva que atende a maior frota de carros do Brasil. Não perca essa oportunidade de expansão!
            </p>
            <button className="parceiros-visibility__btn" onClick={scrollToForm}>
              Cadastre-se agora
            </button>
          </div>
        </div>
      </section>

      {/* ── SUPPORT ──────────────────────────────────────────────────────── */}
      <section className="parceiros-support">
        <div>
          <h2 className="parceiros-support__title">Suporte personalizado</h2>
          <p className="parceiros-support__desc">
            Estamos aqui para garantir que você tenha uma experiência tranquila e eficiente, com orientações estratégicas adaptadas ao seu lava-rápido.
          </p>
          <button className="parceiros-support__btn" onClick={() => navigate('/fale-conosco')}>
            Fale conosco
          </button>
        </div>
        <img src={partnerSupportImg} alt="Suporte WashPass" className="parceiros-support__img" />
      </section>

      <AppFooter variant="dark" />
    </div>
  );
}
